import { fitNavigationRoute, getMap, setNavigationRoute } from './map.js';
import { db } from './db.js';

const SEARCH_BBOX_PADDING_KM = 8;

function mapBbox(padKm = SEARCH_BBOX_PADDING_KM) {
  const map = getMap();
  if (!map) return null;
  let b;
  if (typeof map.getBounds === 'function' && map.getBounds()?.getWest) {
    b = map.getBounds();
  } else if (typeof map.getBounds === 'function') {
    const bounds = map.getBounds();
    if (bounds?.getSouthWest) {
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      b = { getWest: () => sw.lng, getSouth: () => sw.lat, getEast: () => ne.lng, getNorth: () => ne.lat };
    }
  }
  if (!b) return null;
  const lngPad = padKm / 111.32 / Math.max(0.2, Math.cos(((b.getNorth() + b.getSouth()) / 2 * Math.PI) / 180));
  const latPad = padKm / 111.32;
  return [b.getWest() - lngPad, b.getSouth() - latPad, b.getEast() + lngPad, b.getNorth() + latPad];
}

function normalizeQuery(value) {
  return value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9\s]/g, ' ').trim();
}

function matchesQuery(itemText, query) {
  const q = normalizeQuery(query);
  if (!q) return false;
  const text = normalizeQuery(itemText);
  return q.split(/\s+/).every((word) => text.includes(word));
}

function formatAddress(tags = {}) {
  const parts = [];
  if (tags['addr:street']) {
    const num = tags['addr:housenumber'];
    parts.push(num ? `${tags['addr:street']} ${num}` : tags['addr:street']);
  }
  if (tags['addr:postcode'] || tags['addr:city']) {
    parts.push([tags['addr:postcode'], tags['addr:city']].filter(Boolean).join(' '));
  }
  return parts.join(', ') || tags.address || '';
}

async function searchLocalMarkers(query) {
  const bbox = mapBbox();
  if (!bbox) return [];
  const [markers, pois, cachedPlaces] = await Promise.all([
    db.getInBounds(bbox),
    db.getPOIsInBounds(bbox),
    db.searchPlaces(query, 10),
  ]);
  const matchedMarkers = markers
    .filter((m) => matchesQuery(`${m.name} ${m.category} ${m.description || ''}`, query))
    .map((m) => ({
      type: 'marker',
      id: m.id,
      name: m.name || m.category,
      category: m.category,
      description: m.description || '',
      address: '',
      lat: m.lat,
      lng: m.lng,
      display_name: `${m.name || m.category}${m.description ? ` — ${m.description}` : ''}`,
    }));
  const matchedPois = pois
    .filter((p) => matchesQuery(`${p.name} ${p.category} ${p.osmCategory || ''} ${formatAddress(p.tags)}`, query))
    .map((p) => ({
      type: 'poi',
      id: p.id,
      name: p.name || p.category,
      category: p.category,
      osmCategory: p.osmCategory,
      address: formatAddress(p.tags),
      lat: p.lat,
      lng: p.lng,
      display_name: `${p.name || p.category}${p.address ? `, ${p.address}` : ''}`,
    }));
  const placeResults = cachedPlaces.map((p) => ({
    type: 'place',
    place_id: p.id,
    display_name: p.name,
    lat: p.lat,
    lon: p.lng,
    lng: p.lng,
    cached: true,
  }));
  return [...matchedMarkers, ...matchedPois, ...placeResults];
}

export async function searchPlaces(query) {
  const value = query.trim();
  if (!value) return [];
  const local = await searchLocalMarkers(value);
  if (!navigator.onLine) return local;

  try {
    const response = await fetch(`/api/search?q=${encodeURIComponent(value)}`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Search HTTP ${response.status}`);
    const payload = await response.json();
    const remote = (payload.data || []).map((p) => ({ ...p, type: 'place' }));
    remote.forEach((p) => {
      db.upsertPlace({
        id: String(p.place_id || `${p.lat}-${p.lon}`),
        name: p.display_name,
        lat: Number(p.lat),
        lng: Number(p.lon),
      });
    });
    return [...local, ...remote];
  } catch (err) {
    console.warn('[Search] remote failed', err.message);
    return local;
  }
}

const ROUTE_TIMEOUT_MS = 15000;
const ROUTE_BACKENDS = [
  '/api/route',
  'https://routing.openstreetmap.de/routed-bike/route/v1/driving',
];

function buildRouteUrl(base, from, destination) {
  const params = new URLSearchParams({
    fromLng: String(from[0]),
    fromLat: String(from[1]),
    toLng: String(destination[0]),
    toLat: String(destination[1]),
  });
  if (base === '/api/route') return `${base}?${params}`;
  return `${base}/${from[0]},${from[1]};${destination[0]},${destination[1]}?overview=full&geometries=geojson&steps=false`;
}

async function fetchRoute(from, destination) {
  let lastError;
  for (const base of ROUTE_BACKENDS) {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), ROUTE_TIMEOUT_MS);
      const response = await fetch(buildRouteUrl(base, from, destination), {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (!response.ok) throw new Error(`Routing HTTP ${response.status}`);
      const payload = await response.json();
      const route = payload.data?.routes?.[0] || payload.routes?.[0];
      if (route?.geometry) return route;
      throw new Error('No drivable route found.');
    } catch (err) {
      lastError = err;
      console.warn(`[Route] ${base} failed: ${err.message}`);
    }
  }
  throw lastError || new Error('Routing unavailable.');
}

function straightLineRoute(from, destination) {
  return {
    type: 'LineString',
    coordinates: [from, destination],
  };
}

export async function routeToPlace(place, origin = null) {
  const destination = [Number(place.lon), Number(place.lat)];
  const map = getMap();
  const center = origin || map?.getCenter();
  if (!center) throw new Error('Start GPS or wait for the map position before routing.');
  const from = Array.isArray(center) ? center : [center.lng, center.lat];

  const cached = await db.getCachedRoute(from, destination);
  if (cached?.route?.geometry) {
    const normalized = normalizeRoute(cached.route);
    setNavigationRoute(normalized.geometry, destination);
    fitNavigationRoute(normalized.geometry);
    return { route: normalized, destination, cached: true };
  }

  let route;
  let usedFallback = false;
  try {
    route = await fetchRoute(from, destination);
  } catch (err) {
    console.warn('[Route] using straight-line fallback', err.message);
    route = { geometry: straightLineRoute(from, destination), distance: null, duration: null };
    usedFallback = true;
  }

  if (!usedFallback) {
    await db.cacheRoute(from, destination, route);
  }

  const normalized = normalizeRoute(route);
  setNavigationRoute(normalized.geometry, destination);
  fitNavigationRoute(normalized.geometry);
  return { route: normalized, destination, fallback: usedFallback, cached: false };
}

function normalizeRoute(route) {
  const geometry = route.geometry || route;
  const distance = route.distance || 0;
  const duration = route.duration || 0;
  const steps = (route.legs?.[0]?.steps || route.steps || []).map((s) => ({
    name: s.name || '',
    distance: s.distance || 0,
    duration: s.duration || 0,
    maneuver: s.maneuver || { type: 'continue', location: [0, 0] },
  }));
  return { geometry, distance, duration, steps };
}
